// Tracked issues — `GET /v1/issues/:id` and the two forced overrides on it
// (`<IssueDetailDrawer/>`, arc 101 slice 06), plus the rows the repo workspace's
// Issues tab lists.
//
// Its own module because it is its own `/v1` family AND the only STATEFUL half
// of the repo workspace: the overrides rewrite `repoIssueRows` in place, so this
// file — not `repo-workspace-mocks.ts` — is what `mock-state.ts` registers a
// reset for. Neither write answers a bare success: each MODELS the outcome its
// route computes, because whatever a write claims, the follow-up refetch has to
// be able to see it.

import {
  type EscalationCategory,
  HARDCODED_ALWAYS_SET,
  type OverviewSessionWire,
} from '@developerz/domain';
import { webhooksList } from './account-webhooks-mocks';
import type { EscalateResult, HandoffResult } from './issue-actions';
import type { RepoIssueRow, RepoIssuesPage } from './issues';
import { repos } from './mock-repo-fixtures';
import { HOUR_MS, MIN_MS, sinceMs } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * One tracked issue as this family STORES it: the wire row, with the agent's
 * last action held as a thunk. Both screens that print it — the table's LAST
 * ACTION cell and the drawer's facts — print it through `ago()`, so it is an AGE,
 * and an age is built per read (`sinceMs`'s rule, `mock-session.ts`): this array
 * is module state, and a dev tab outlives a day. An `at()` here was also
 * FUTURE-dated against the marketing capture's pinned clock (2026-07-17), where
 * #842 printed "in 2 days".
 */
type IssueFixture = Omit<RepoIssueRow, 'last_agent_action_at'> & {
  readonly lastActedAt: (() => string) | null;
};

const wire = ({ lastActedAt, ...row }: IssueFixture): RepoIssueRow => ({
  ...row,
  last_agent_action_at: lastActedAt?.() ?? null,
});

// Tracked issues (RepoDetail's Issues tab + drawer, arc 101 slice 06), in the
// list's default `-github_number` order. Each row's `last_session_id` is its
// own triage run, answered by `issueSession` below; #842's `sess_t842` is also
// the "Triage issue #842" row in `repoOverview`/`repoSessions`.
//
// EVERY ROW IS TRIAGED, and so carries a session and an action time: the triage
// writeback (`recordTriageOutcome`, `packages/db/src/queries/issues.ts`) is the
// only writer of `qualification` and stamps all three together, so a qualified
// row with neither is a shape the server cannot produce. One per real verdict
// (`Qualification`, `packages/domain/src/entities/issue.ts`) except `unknown`,
// which is reserved for the never-classified path. The pre-cache `''` title
// (#980) is `RepoDetail.issues.test.tsx`'s to pin, not a demo's to show.
//
// Mutable, and rebuilt into a page per read: the forced overrides below write
// to it. A mock that answers "dispatched" and leaves the row untouched would be
// modelling an outcome the server never produces.
export const repoIssueRows: IssueFixture[] = [
  {
    id: 'issue_861',
    repo_id: 'repo_1',
    github_number: 861,
    github_id: 900861,
    title: 'Support per-route rate-limit overrides',
    state: 'open',
    qualification: 'feature',
    handoff_status: 'accepted',
    last_session_id: 'sess_t861',
    lastActedAt: () => sinceMs(HOUR_MS),
  },
  {
    id: 'issue_857',
    repo_id: 'repo_1',
    github_number: 857,
    github_id: 900857,
    title: '429 responses carry no Retry-After header',
    state: 'closed',
    qualification: 'dupe',
    handoff_status: null,
    last_session_id: 'sess_t857',
    lastActedAt: () => sinceMs(4 * HOUR_MS),
  },
  {
    id: 'issue_842',
    repo_id: 'repo_1',
    github_number: 842,
    github_id: 900842,
    title: 'Fix login flake',
    state: 'open',
    qualification: 'bug',
    handoff_status: 'pending',
    last_session_id: 'sess_t842',
    lastActedAt: () => sinceMs(2 * HOUR_MS),
  },
  {
    id: 'issue_829',
    repo_id: 'repo_1',
    github_number: 829,
    github_id: 900829,
    title: 'Upstream timeouts surface as 500 instead of 504',
    state: 'open',
    qualification: 'bug',
    handoff_status: null,
    last_session_id: 'sess_t829',
    lastActedAt: () => sinceMs(6 * HOUR_MS),
  },
  {
    id: 'issue_803',
    repo_id: 'repo_1',
    github_number: 803,
    github_id: 900803,
    title: 'How do I set a per-upstream timeout?',
    state: 'closed',
    qualification: 'question',
    handoff_status: null,
    last_session_id: 'sess_t803',
    lastActedAt: () => sinceMs(9 * HOUR_MS),
  },
];

/** Open forced escalations, keyed `<issueId>:<category>` — the dedupe ledger (#500). */
const openEscalations = new Set<string>();
export const repoIssuesPage = (): RepoIssuesPage => ({
  data: repoIssueRows.map(wire),
  next_cursor: null,
});

/**
 * The session a row's "View session" link opens, spelled as the Home feed
 * spells one so `sessionDetail` (`sessions-mocks.ts`) can answer it: the run
 * that decided this issue, finished when the row says the agent last acted.
 */
export function issueSession(sessionId: string): OverviewSessionWire | undefined {
  const issue = repoIssueRows.find((i) => i.last_session_id === sessionId);
  const finished = issue?.lastActedAt?.();
  if (issue === undefined || finished === undefined) return undefined;
  const repo = repos.data.find((r) => r.id === issue.repo_id);
  if (repo === undefined) throw new Error(`${issue.id}: no repo fixture holds ${issue.repo_id}`);
  return {
    id: sessionId,
    repo_full_name: repo.full_name,
    trigger: 'issue',
    entity_kind: 'issue',
    entity_number: issue.github_number,
    // The feed's headline falls back to the raw trigger, never null.
    entity_title: issue.title ?? 'issue',
    outcome: 'completed',
    started_iso: new Date(Date.parse(finished) - 3 * MIN_MS).toISOString(),
    finished_iso: finished,
  };
}

/**
 * Answer `/v1/issues/*`, or `null` for "not my route".
 *
 * An id under the prefix that no row holds REJECTS: an owned prefix with a
 * missing row is still a missing handler, and answering a shaped body the
 * drawer would paint as truth is exactly what the fallthrough exists to prevent.
 */
export function repoIssueMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  if (!path.startsWith('/v1/issues/')) return null;
  const rest = (path.split('?')[0] ?? path).slice('/v1/issues/'.length);
  const [issueId = '', action = ''] = rest.split('/');
  const at_ = repoIssueRows.findIndex((i) => i.id === issueId);
  const issue: IssueFixture | undefined = repoIssueRows[at_];
  if (!issue) return unhandled(path, method);
  if (action === '') return Promise.resolve(wire(issue));
  if (action === 'escalate' && method === 'POST')
    return Promise.resolve(escalateIssue(issueId, init));
  if (action === 'handoff' && method === 'POST')
    return Promise.resolve(handoffIssue(issue, at_, init));
  return unhandled(path, method);
}

function unhandled(path: string, method: string): Promise<never> {
  return unhandledMockRoute(method, path);
}

/**
 * `fires_immediately` off the same `HARDCODED_ALWAYS_SET` the route reads, and a
 * repeat force of the same (issue, category) is `deduped` onto the open incident
 * — nothing new fires. Whatever a write claims, the follow-up refetch has to be
 * able to see it.
 */
function escalateIssue(issueId: string, init?: RequestInit): EscalateResult {
  const body = init?.body ? (JSON.parse(String(init.body)) as { category?: string }) : {};
  const category = body.category ?? '';
  const key = `${issueId}:${category}`;
  const deduped = openEscalations.has(key);
  openEscalations.add(key);
  return {
    ok: true,
    escalationId: `esc_${issueId}_${category}`,
    fires_immediately: HARDCODED_ALWAYS_SET.has(category as EscalationCategory),
    ...(deduped ? { deduped: true } : {}),
  };
}

/**
 * Refused as `already_pending` while one is in flight, and as `no_webhook` when
 * the named target is not an `issue.handoff` subscriber. Only a dispatch rewrites
 * the row, and it does so BEFORE replying — so the drawer's re-read and the
 * patched table row show stored truth, not the client's hope.
 *
 * A dispatch MINTS NO SESSION. The route stamps `pending` and nothing else
 * (`setIssueHandoffPending`), queues a webhook delivery to the user's coding
 * agent, and answers with the session it audited the act on — the issue's own
 * (`forceHandoff` in `apps/api/src/index.ts`). A `sess_forced_<n>` minted here
 * opened as a run that had `completed` at the instant it was queued.
 */
function handoffIssue(issue: IssueFixture, at_: number, init?: RequestInit): HandoffResult {
  const body = init?.body ? (JSON.parse(String(init.body)) as { webhook_ref?: string }) : {};
  const target = body.webhook_ref ?? null;
  // Blank → the account's `issue.handoff` subscriber answers for it.
  const resolved = webhooksList.data.some(
    (w) => (target === null || w.name === target) && w.events.includes('issue.handoff'),
  );
  if (issue.handoff_status === 'pending') {
    return { ok: true, sessionId: issue.last_session_id, queued: false, reason: 'already_pending' };
  }
  if (!resolved) {
    return { ok: true, sessionId: issue.last_session_id, queued: false, reason: 'no_webhook' };
  }
  repoIssueRows[at_] = { ...issue, handoff_status: 'pending' };
  return { ok: true, sessionId: issue.last_session_id, queued: true };
}

/**
 * Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`).
 * A spread, not `structuredClone`: the rows hold a thunk, and a row is flat.
 */
const PRISTINE_REPOISSUEROWS = repoIssueRows.map((row) => ({ ...row }));
const PRISTINE_OPENESCALATIONS: readonly string[] = [...openEscalations];

export function resetRepoIssueMocks(): void {
  repoIssueRows.splice(
    0,
    repoIssueRows.length,
    ...PRISTINE_REPOISSUEROWS.map((row) => ({ ...row })),
  );
  openEscalations.clear();
  for (const entry of PRISTINE_OPENESCALATIONS) openEscalations.add(entry);
}
